import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Pagination } from "@mui/material";
import moment from "moment";
import DataTable from "../../common/DataTable";
import SearchBar from "../../common/filters/SearchBar";
import CustomDateRangePicker from "../../pages/selller/dashboards/components/custom-date-range";

import { fetchInvoices } from "../../api/invoice";
import { Checkbox } from "@mui/material";
import colors from "../../theme/Colours";

import {
  containerStyle,
  searchAndSortContainer,
  mainContentStyle,
  paginationContainerStyle,
  paginationCaptionStyle,
} from "./InvoiceHistory.styles";
import SortBy from "../../common/filters/SortBy";
import { useOutsideClose } from "../../hooks/useOutsideClose";
import {
  SortBYContainerStyle,
  downloadCsvButtonStyle,
  downloadIconStyle,
} from "../customerManagement/DisplayCustomers.styles";
import CloudDownloadIcon from "../../assets/download-cloud-02.svg";
import Toast from "../../common/Toast";

type SortTypes = "newest" | "oldest";

export const InvoiceHistory = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<SortTypes>("newest");

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 0,
  });

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const options = ["Newest", "Oldest"];
  const [sortBy, setSortBy] = useState<string>("");
  const [openFilter, setOpenFilter] = useState<"sort" | "date" | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastError, setToastError] = useState(false);
  const filterContainerRef = useRef<HTMLDivElement>(null);

  useOutsideClose(filterContainerRef, openFilter !== null, () =>
    setOpenFilter(null)
  );

  const firstRender = useRef(true);

  const handleSelectAll = () => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setSelectedInvoices([]);
    } else {
      setSelectAllPages(true);
      setSelectedInvoices([]);
    }
  };
  const columns = [
    {
      label: (
        <Checkbox
          checked={
            selectAllPages ||
            (invoices.length > 0 &&
              invoices.every((inv) => selectedInvoices.includes(inv.exciseID)))
          }
          indeterminate={
            !selectAllPages &&
            selectedInvoices.length > 0 &&
            selectedInvoices.length < invoices.length
          }
          onChange={handleSelectAll}
          sx={{
            "&.Mui-checked": { color: colors.primaryButtonOrange },
            "&.MuiCheckbox-indeterminate": {
              color: colors.primaryButtonOrange,
            },
          }}
        />
      ),
      field: "checkbox",
      align: "center" as const,
    },
    { label: "Customer Id", field: "customerId" },
    { label: "Name", field: "customerName" },
    { label: "Invoice Number", field: "invoiceNumber" },
    { label: "Invoice Date", field: "invoiceDate" },
    { label: "Download", field: "download" },
  ];

  const fetchInvoiceData = async (
    download: string,
    search?: string,
    page?: number
  ) => {
    const apiSortOrder = sortOrder === "newest" ? "DESC" : "ASC";
    const adjustedFromDate = from
      ? moment(from).startOf("day").toISOString()
      : undefined;

    const adjustedToDate = to
      ? moment(to).endOf("day").toISOString()
      : undefined;

    return await fetchInvoices({
      page: download === "true" ? undefined : page,
      limit: download === "true" ? undefined : pagination.rowsPerPage,
      searchTerm: search,
      startDate: adjustedFromDate,
      endDate: adjustedToDate,
      sortOrder: apiSortOrder,
    });
  };

  const loadInvoices = async (page: number, search?: string) => {
    try {
      const response = await fetchInvoiceData("false", search, page);

      const data = response?.data?.data;

      if (!data) return;

      setPagination({
        page: data.pagination.currentPage,
        rowsPerPage: data.pagination.limit,
        totalRows: data.pagination.totalRecords,
        totalPages: data.pagination.totalPages,
      });

      const formatted = data.invoices.map((invoice: any) => ({
        clientCode: invoice.clientCode,
        exciseID: invoice.exciseID,
        customerName: invoice.outletName,
        invoiceDate: moment(invoice.updatedAt).format("DD MMM YY hh:mm:ss A"),
        url: invoice.url,
      }));

      setInvoices(formatted);
    } catch (error) {
      console.error("Error fetching invoices", error);
    }
  };

  const toggleInvoice = (invoiceId: string) => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setSelectedInvoices(
        invoices.map((inv) => inv.exciseID).filter((id) => id !== invoiceId)
      );
    } else {
      setSelectedInvoices((prev) =>
        prev.includes(invoiceId)
          ? prev.filter((id) => id !== invoiceId)
          : [...prev, invoiceId]
      );
    }
  };

  useEffect(() => {
    if (firstRender.current) {
      loadInvoices(1, "");
      firstRender.current = false;
    }
  }, []);
  useEffect(() => {
    if (!firstRender.current) {
      loadInvoices(1, searchTerm);
    }
  }, [from, to]);
  useEffect(() => {
    if (!firstRender.current) {
      loadInvoices(1, searchTerm);
    }
  }, [sortOrder]);
  useEffect(() => {
    if (!sortBy) {
      setSortOrder("newest");
      return;
    }

    setSortOrder(sortBy.toLowerCase() === "oldest" ? "oldest" : "newest");
  }, [sortBy]);

  const bulkDownload = async () => {
    if (!selectAllPages && selectedInvoices.length === 0) {
      setToastMessage("Select at least one invoice");
      setToastError(true);
      setShowToast(true);
      return;
    }

    let selectedData = invoices;

    try {
      if (selectAllPages) {
        let allInvoices: any[] = [];
        let currentPage = 1;
        let totalPages = 1;

        while (currentPage <= totalPages) {
          const response = await fetchInvoiceData(
            "false",
            searchTerm,
            currentPage
          );

          const data = response?.data?.data;

          if (!data) break;

          totalPages = data.pagination.totalPages;

          const pageInvoices = data.invoices.map((invoice: any) => ({
            exciseID: invoice.exciseID,
            url: invoice.url,
          }));

          allInvoices = [...allInvoices, ...pageInvoices];

          currentPage++;
        }

        selectedData = allInvoices;
      } else {
        selectedData = invoices.filter((inv) =>
          selectedInvoices.includes(inv.exciseID)
        );
      }

      for (const invoice of selectedData) {
        try {
          const response = await fetch(invoice.url);
          const blob = await response.blob();

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `invoice-${invoice.exciseID}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(link.href);

          await new Promise((resolve) => setTimeout(resolve, 400));
        } catch (error) {
          console.error(
            `Error downloading invoice ${invoice.exciseID}:`,
            error
          );
        }
      }
    } catch (err) {
      console.error("Bulk download failed", err);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    loadInvoices(1, value);
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadInvoices(newPage, searchTerm);
  };

  const { page, rowsPerPage, totalRows, totalPages } = pagination;

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + invoices.length;

  const tableData = invoices.map((invoice) => ({
    checkbox: (
      <Checkbox
        checked={selectAllPages || selectedInvoices.includes(invoice.exciseID)}
        onChange={() => toggleInvoice(invoice.exciseID)}
        sx={{ "&.Mui-checked": { color: colors.primaryButtonOrange } }}
      />
    ),

    customerId: invoice.clientCode ?? "N/A",
    customerName: invoice.customerName,
    invoiceNumber: invoice.exciseID ?? "N/A",
    invoiceDate: invoice.invoiceDate,

    download: (
      <button
        onClick={() => window.open(invoice.url, "_blank")}
        style={{
          backgroundColor: "#e9e9e9",
          color: "#3c3c3c",
          border: "1px solid #6b6b6b",
          borderRadius: "24px",
          padding: "6px 28px",
          fontSize: "14px",
          fontWeight: 500,
          cursor: "pointer",
          display: "inline-block",
        }}
      >
        Download
      </button>
    ),
  }));

  return (
    <>
      <Box sx={searchAndSortContainer}>
        <Box>
          <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
        </Box>
        <Box
          ref={filterContainerRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
            width: { xs: "100%", md: "auto" },
            zIndex: openFilter === "date" ? 998 : undefined,
          }}
        >
          {/* Sort By */}
          <SortBy
            containerSx={SortBYContainerStyle}
            options={options}
            selected={sortBy}
            setSelected={setSortBy}
            variant="seller"
            hideOptions
            isOpen={openFilter === "sort"}
            onToggle={(open) => setOpenFilter(open ? "sort" : null)}
          />

          {/* Date Range */}
          <CustomDateRangePicker
            fromDate={from}
            toDate={to}
            setFromDate={setFrom}
            setToDate={setTo}
            placeholder="Select date range"
            disableFuture
            showReset
            sx={{
              height: "38px",
              width: "220px",
              borderRadius: "4px",
            }}
          />

          <button
            onClick={bulkDownload}
            style={downloadCsvButtonStyle as React.CSSProperties}
          >
            <img
              src={CloudDownloadIcon}
              alt="Download"
              style={downloadIconStyle}
            />
            Download
          </button>
        </Box>
      </Box>

      <Box sx={containerStyle}>
        <Box sx={mainContentStyle}>
          <DataTable
            columns={columns}
            data={tableData}
            emptyStateMessage="No invoices available"
          />
        </Box>

        <Box sx={paginationContainerStyle}>
          <Typography variant="subtitle2" sx={paginationCaptionStyle}>
            Showing data {startIndex + 1} to {endIndex} of {totalRows} entries
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => handleChangePage(newPage)}
            shape="rounded"
            variant="outlined"
          />
        </Box>
      </Box>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={toastError}
      />
    </>
  );
};

export default InvoiceHistory;
